import axios from "axios";

import requestHeadersInterceptor from "../components/shared/network/interceptors/requests";
import { getServerIp } from "./locationService";

/** True when the request was aborted or cancelled by the client (Axios / fetch). */
export function isDashcamRequestCancelled(error) {
  return axios.isCancel?.(error) || error?.code === "ERR_CANCELED";
}

let dashcamClient;

/** Axios instance for Dashcam routed through the manager-api proxy at /api/dashcam.
 * Omits the generic response error interceptor — callers handle all errors via mapDashcamHttpError. */
export function getDashcamClient() {
  if (!dashcamClient) {
    const apiBase = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");
    dashcamClient = axios.create({
      baseURL: `${apiBase}/dashcam`,
      // 35s gives enough room to receive a 504 from the proxy (30s read timeout + connect window).
      timeout: 35000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    dashcamClient.interceptors.request.use(requestHeadersInterceptor);
  }
  return dashcamClient;
}

/** Map Manager video recorder `sub_type` to Dashcam stream type (`main` or `sub`). */
export function streamTypeFromVideoRecorderSubType(subType) {
  const normalized = String(subType ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "main" || normalized === "sub") {
    return normalized;
  }
  return null;
}

/** Replace `{}` in device_host with the channel number for IP camera templates (e.g. 192.168.1.{}). */
export function applyDeviceHostChannelPlaceholder(deviceHost, channel) {
  if (deviceHost == null || typeof deviceHost !== "string" || !deviceHost.includes("{}")) {
    return deviceHost;
  }
  const n = Number(channel);
  const replacement = Number.isFinite(n) && n >= 1 ? String(n) : "1";
  return deviceHost.replaceAll("{}", replacement);
}

/** Smallest finite channel_number from the recorder, or null if none. */
function minChannelNumberFromVideoRecorder(videoRecorder) {
  const chs = videoRecorder?.channels;
  if (!Array.isArray(chs)) return null;
  const numbers = chs
    .filter((c) => c != null && typeof c === "object")
    .map((c) => Number(c.channel_number))
    .filter((n) => Number.isFinite(n) && n >= 1);
  if (numbers.length === 0) return null;
  return Math.min(...numbers);
}

/** Channel used to resolve `{}` in device_host when no per-request channel applies. */
export function defaultHostPlaceholderChannel(videoRecorder) {
  return minChannelNumberFromVideoRecorder(videoRecorder) ?? 1;
}

/** First non-empty inference machine hostname across channels (order preserved). */
function firstInferenceMachineHostname(videoRecorder) {
  const chs = videoRecorder?.channels;
  if (!Array.isArray(chs)) return "";
  const row = chs.find((c) => {
    if (c == null || typeof c !== "object" || !c.inference_machine_data) return false;
    return Boolean(String(c.inference_machine_data.hostname || "").trim());
  });
  return row ? String(row.inference_machine_data.hostname || "").trim() : "";
}

/** Resolve Dashcam store_id from explicit storeId or the video recorder's store FK. */
export function resolveStoreIdForDashcam(storeId, videoRecorder) {
  if (storeId != null && String(storeId).trim() !== "") {
    return String(storeId);
  }
  if (videoRecorder?.store != null && String(videoRecorder.store).trim() !== "") {
    return String(videoRecorder.store);
  }
  return "";
}

/** Coerce DVR HTTP/RTSP port fields: N/A and invalid values become null. */
export function coercePort(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 65535) {
    return value;
  }
  if (typeof value === "string" && value.trim().toUpperCase() === "N/A") return null;
  const n = Number(value);
  if (Number.isInteger(n) && n >= 1 && n <= 65535) return n;
  return null;
}

/**
 * Build a Dashcam CamerasRequest JSON body from a store video recorder row.
 * `machine_name` is the first non-empty inference hostname on any channel (list order); it enables Manager
 * VPN lookup so Dashcam can send `machine_vpn_ip`. Without it, private `device_host` is rejected
 * by Dashcam SSRF rules in cloud mode.
 */
export function buildCamerasRequestBody(videoRecorder, { localInference = false } = {}) {
  const httpKey = localInference ? "device_api_port" : "device_api_port_redirected";

  const apiPort = coercePort(videoRecorder[httpKey] ?? videoRecorder.device_api_port);

  const machineName = firstInferenceMachineHostname(videoRecorder);

  const body = {
    device_host: videoRecorder.device_ip,
    username: videoRecorder.username,
    password: videoRecorder.password,
    machine_name: machineName,
    device_api_port: apiPort,
  };

  return body;
}

/** Extract VPN IP from Manager get_vpn_ip response (server_ip). */
function parseVpnIpFromManagerResponse(data) {
  if (data == null) {
    return "";
  }
  if (typeof data === "number" && Number.isFinite(data)) {
    return String(data);
  }
  if (typeof data === "string") {
    const t = data.trim();
    return t.length ? t : "";
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    const ip = data.server_ip;
    if (typeof ip === "string" && ip.trim()) {
      return ip.trim();
    }
  }
  return "";
}

/**
 * Extract Dashcam cloud routing fields from Manager get_vpn_ip response.
 * Manager reports `machine_type` (nuc/gpu/cloud/unknown) and `cloud_region`; Dashcam takes
 * `machine_type` + `machine_region` and only then routes through the region's jump host.
 * Returns `{}` for anything but a cloud machine with a region — Dashcam 422s on a cloud
 * machine_type without a region, so both fields travel together or not at all.
 */
function parseCloudTopologyFromManagerResponse(data) {
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }
  if (data.machine_type !== "cloud") {
    return {};
  }
  const region = typeof data.cloud_region === "string" ? data.cloud_region.trim() : "";
  if (!region) {
    return {};
  }
  return { machine_type: "cloud", machine_region: region };
}

/** Copy the cloud routing fields onto a payload derived from `base`; empty for non-cloud machines. */
function cloudTopologyFields(base) {
  if (base.machine_type !== "cloud") {
    return {};
  }
  return { machine_type: base.machine_type, machine_region: base.machine_region };
}

/**
 * Merge Manager VPN IP when an inference machine hostname is set and Manager resolves a jump host.
 * `machine_vpn_ip` is omitted from the JSON otherwise (SaaS / direct access; Dashcam treats it as optional).
 * The same lookup carries the cloud topology, so cloud stores route through the regional jump host
 * instead of being dialled from the Dashcam pod, which the store firewall drops.
 */
export async function buildCamerasRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false } = {},
  { signal } = {}
) {
  const body = buildCamerasRequestBody(videoRecorder, { localInference });
  return { ...body, ...(await resolveMachineRouting(body.machine_name, { signal })) };
}

/**
 * Dashcam routing fields for an inference machine: `machine_name`, plus `machine_vpn_ip` and the cloud
 * topology when Manager resolves them. A failed lookup degrades to the bare name (direct access).
 */
export async function resolveMachineRouting(machineName, { signal } = {}) {
  const name = typeof machineName === "string" ? machineName.trim() : "";
  if (!name) {
    return { machine_name: typeof machineName === "string" ? machineName : "" };
  }
  try {
    const { data } = await getServerIp(name, { signal });
    const machineVpnIp = parseVpnIpFromManagerResponse(data);
    return {
      machine_name: name,
      ...(machineVpnIp ? { machine_vpn_ip: machineVpnIp } : {}),
      ...parseCloudTopologyFromManagerResponse(data),
    };
  } catch (err) {
    if (isDashcamRequestCancelled(err)) {
      throw err;
    }
    return { machine_name: name };
  }
}

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first.msg === "string") return first.msg;
  }
  return null;
}

/** Map Dashcam API / Axios errors to user-facing messages (no 429 retry). */
export function mapDashcamHttpError(error) {
  if (isDashcamRequestCancelled(error)) {
    return "Request cancelled.";
  }

  const status = error?.response?.status;
  const detail = formatDetail(error?.response?.data?.detail);

  switch (status) {
    case 401:
      return "Unauthorized — your session may have expired. Try refreshing the page.";
    case 429:
      return "Dashcam rate limit reached. Wait a minute before trying again.";
    case 502:
      // 502 from the proxy means the dashcam service was unreachable; relayed 502 from dashcam
      // itself means a DVR/network failure. Either way `detail` carries the specific message.
      return detail || "Dashcam service unreachable (bad gateway).";
    case 503:
      return detail || "Dashcam is temporarily unavailable.";
    case 504:
      return detail || "Dashcam did not respond in time (gateway timeout).";
    default:
      break;
  }

  if (detail && status && status >= 400 && status < 600) {
    return detail;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return "Unexpected error while calling Dashcam.";
}

// Human-readable labels for the OperationOutcome.failure_reason enum returned by Dashcam.
const DASHCAM_FAILURE_REASON_LABELS = {
  tunnel: "VPN tunnel error",
  network: "Network error reaching device",
  port_discovery: "Could not discover device port",
  auth: "Authentication rejected by device",
  protocol: "Device protocol error",
  channel_not_found: "Channel not found on device",
  permission: "Device denied permission",
  device_not_activated: "Device not activated",
  endpoint_not_supported: "Operation not supported by device",
};

/** True when a Dashcam response envelope reports a non-success outcome. */
export function isDashcamOutcomeFailure(data) {
  return data != null && typeof data === "object" && data.status !== "success";
}

/**
 * Build a user-facing message from a failed Dashcam OperationOutcome envelope.
 * Returns null when the response is not a failure. Uses the humanized failure_reason first,
 * then description, falling back to a generic message.
 */
export function formatDashcamOutcomeMessage(data) {
  if (!isDashcamOutcomeFailure(data)) return null;
  const reason = data.failure_reason;
  const label = reason ? DASHCAM_FAILURE_REASON_LABELS[reason] ?? String(reason) : null;
  const description = typeof data.description === "string" && data.description.trim() ? data.description.trim() : null;
  if (label && description) return `${label}: ${description}`;
  return label || description || "Dashcam reported a failure with no further details.";
}

/**
 * POST helper with LLM try-harder retry: if Dashcam returns 400 with a
 * "confirm_unsafe" hint (no code adapter or code adapter failed), retry
 * once with confirm_unsafe=true so the LLM fallback path is attempted.
 * Any other error is re-thrown immediately.
 */
async function postWithLlmFallback(endpoint, payload, { signal } = {}) {
  const client = getDashcamClient();
  try {
    return await client.post(endpoint, payload, { signal });
  } catch (err) {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 400 && typeof detail === "string" && detail.includes("confirm_unsafe")) {
      return client.post(endpoint, { ...payload, confirm_unsafe: true }, { signal });
    }
    throw err;
  }
}

/** POST /capabilities with optional AbortSignal. */
export function postCapabilities(payload, { signal } = {}) {
  const client = getDashcamClient();
  return client.post("/capabilities", payload, { signal });
}

/** POST /cameras (gather current config) with optional AbortSignal. */
export function postCameras(payload, { signal } = {}) {
  const client = getDashcamClient();
  return client.post("/cameras", payload, { signal });
}

/** POST /screenshot (capture one JPEG frame) with optional AbortSignal. Returns { image_base64 }. */
export function postScreenshot(payload, { signal } = {}) {
  const client = getDashcamClient();
  return client.post("/screenshot", payload, { signal });
}

// Build one POST /screenshot body from a pre-resolved `base` (so a scan resolves
// the VPN IP once, not per channel). `rtspPath`, when given, skips path discovery.
export function buildScreenshotRequestBody(
  base,
  videoRecorder,
  { localInference = false, channel, streamType = "main", rtspPath } = {}
) {
  const rtspKey = localInference ? "device_rtsp_port" : "device_rtsp_port_redirected";
  const deviceRtspPort = coercePort(videoRecorder[rtspKey] ?? videoRecorder.device_rtsp_port) ?? 554;
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_rtsp_port: deviceRtspPort,
    channel: { slot_number: channel, stream_type: streamType },
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (rtspPath != null && String(rtspPath).trim() !== "") payload.rtsp_path = String(rtspPath);
  return payload;
}

/** POST /set-resolution (apply resolution on a DVR channel/stream) with optional AbortSignal. */
export function postSetResolution(payload, { signal } = {}) {
  return postWithLlmFallback("/set-resolution", payload, { signal });
}

/** Parse a "WxH" / "WXH" string (case-insensitive) into {width, height}; null on malformed input. */
export function parseResolutionString(value) {
  if (typeof value !== "string") return null;
  const m = value.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/);
  if (!m) return null;
  const width = Number(m[1]);
  const height = Number(m[2]);
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) return null;
  return { width, height };
}

/** Build POST /set-resolution JSON body with VPN tunnel fields. */
export async function buildSetResolutionRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType, width, height, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    channel: { slot_number: channel, stream_type: streamType },
    width,
    height,
  };
  if (base.machine_vpn_ip) {
    payload.machine_vpn_ip = base.machine_vpn_ip;
  }
  if (storeId != null && String(storeId).trim() !== "") {
    payload.store_id = String(storeId);
  }
  return payload;
}

/** POST /set-bitrate with optional AbortSignal. */
export function postSetBitrate(payload, { signal } = {}) {
  return postWithLlmFallback("/set-bitrate", payload, { signal });
}

/**
 * Parse bitrate integer (kbps). Rejects decimals and non-integers. Returns null on invalid.
 * Range enforcement happens in the handler against device min/max.
 */
export function parseBitrateKbps(value) {
  const s = String(value ?? "").trim();
  if (!/^\d+$/.test(s)) return null;
  return parseInt(s, 10);
}

/** Build POST /set-bitrate JSON body with VPN tunnel fields (mirrors buildCapabilitiesRequestBodyWithVpnTunnel). */
export async function buildSetBitrateRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType, bitrate, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    channel: { slot_number: channel, stream_type: streamType },
    bitrate,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

/** POST /set-fps with optional AbortSignal. */
export function postSetFps(payload, { signal } = {}) {
  return postWithLlmFallback("/set-fps", payload, { signal });
}

/** Parse FPS positive integer. Returns null on invalid input. Range is device-constrained by supported_fps. */
export function parseFps(value) {
  const s = String(value ?? "").trim();
  if (!/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (n < 1) return null;
  return n;
}

/** Build POST /set-fps JSON body with VPN tunnel fields. */
export async function buildSetFpsRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType, fps, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    channel: { slot_number: channel, stream_type: streamType },
    fps,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

/** POST /set-bitrate-control with optional AbortSignal. */
export function postSetBitrateControl(payload, { signal } = {}) {
  return postWithLlmFallback("/set-bitrate-control", payload, { signal });
}

/** Validate bitrate control mode. Returns "cbr" or "vbr"; null on anything else. */
export function parseBitrateControlMode(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  if (s === "cbr" || s === "vbr") return s;
  return null;
}

/** Build POST /set-bitrate-control JSON body with VPN tunnel fields. */
export async function buildSetBitrateControlRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType, mode, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    channel: { slot_number: channel, stream_type: streamType },
    mode,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

const VALID_ENCODINGS = ["h264", "h265"];

/** Parse and normalize a codec string. Accepts "h264", "H264", "H.264", " h.265 ", etc.
 * Returns null for anything outside {h264, h265}.
 */
export function parseEncoding(value) {
  if (typeof value !== "string") return null;
  const s = value.trim().toLowerCase().replace(/\./g, "");
  if (!VALID_ENCODINGS.includes(s)) return null;
  return s;
}

/** POST /set-encoding with optional AbortSignal. */
export function postSetEncoding(payload, { signal } = {}) {
  return postWithLlmFallback("/set-encoding", payload, { signal });
}

/** Build POST /set-encoding JSON body with VPN tunnel fields. */
export async function buildSetEncodingRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType, codec, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, channel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    channel: { slot_number: channel, stream_type: streamType },
    codec,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

/** POST /set-dhcp with optional AbortSignal. */
export function postSetDhcp(payload, { signal } = {}) {
  return postWithLlmFallback("/set-dhcp", payload, { signal });
}

/**
 * Convert a gather-response DHCP value ("dynamic"/"static") to the /set-dhcp mode
 * expected by the API ("dhcp"/"static"). Returns null on unrecognized input.
 * The gather response uses "dynamic"; the set API uses "dhcp" for the same concept.
 */
export function parseDhcpMode(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  if (s === "dynamic") return "dhcp";
  if (s === "static") return "static";
  return null;
}

/**
 * Build POST /set-dhcp JSON body.
 * /set-dhcp is device-scoped — no channel/stream fields are included.
 */
export async function buildSetDhcpRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, mode, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const hostChannel = defaultHostPlaceholderChannel(videoRecorder);
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, hostChannel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
    mode,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

/** POST /reboot with optional AbortSignal. */
export function postReboot(payload, { signal } = {}) {
  const client = getDashcamClient();
  return client.post("/reboot", payload, { signal });
}

/** Build POST /reboot JSON body. /reboot is device-scoped — no channel/stream fields. */
export async function buildRebootRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, storeId } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const hostChannel = defaultHostPlaceholderChannel(videoRecorder);
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, hostChannel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    device_api_port: base.device_api_port,
  };
  if (base.machine_vpn_ip) payload.machine_vpn_ip = base.machine_vpn_ip;
  if (storeId != null && String(storeId).trim() !== "") payload.store_id = String(storeId);
  return payload;
}

/**
 * ChannelRef[] restricting a /cameras request to the recorder's VM-configured channels, so Dashcam
 * only queries those slots instead of sweeping the whole device. `stream_type` is left unset so each
 * slot returns both main and sub. Returns null — meaning "omit `channels`, sweep everything" — for IP
 * cameras (each channel is a separate `{}` host) and for recorders with no finite channel_number.
 */
export function channelRefsFromVideoRecorder(videoRecorder) {
  if (videoRecorder?.video_recorder_type_name === "IP") return null;
  const channels = videoRecorder?.channels;
  if (!Array.isArray(channels) || channels.length === 0) return null;
  const slots = [
    ...new Set(channels.map((c) => Number(c?.channel_number)).filter((n) => Number.isFinite(n) && n >= 1)),
  ];
  if (slots.length === 0) return null;
  slots.sort((a, b) => a - b);
  return slots.map((slot) => ({ slot_number: slot }));
}

/**
 * Build POST /cameras JSON (CamerasRequest) with optional VPN tunnel fields.
 * `channels` restricts the query to VM-configured slots (omitted to sweep all — see
 * channelRefsFromVideoRecorder). `machine_vpn_ip` is only included for on-prem (tunnel) paths when
 * Manager returns a VPN IP.
 */
export async function buildGatherCamerasRequestBodyWithVpnTunnel(
  videoRecorder,
  storeId,
  { localInference = false } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const { machine_vpn_ip: machineVpnIp, ...baseWithoutVpn } = base;
  const rtspKey = localInference ? "device_rtsp_port" : "device_rtsp_port_redirected";
  const rtspPort = coercePort(videoRecorder[rtspKey] ?? videoRecorder.device_rtsp_port);
  const resolvedRtsp = rtspPort ?? 554;
  const hostChannel = defaultHostPlaceholderChannel(videoRecorder);
  const channels = channelRefsFromVideoRecorder(videoRecorder);
  return {
    ...baseWithoutVpn,
    store_id: resolveStoreIdForDashcam(storeId, videoRecorder),
    device_rtsp_port: resolvedRtsp,
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, hostChannel),
    ...(channels ? { channels } : {}),
    ...(machineVpnIp ? { machine_vpn_ip: machineVpnIp } : {}),
  };
}

// Dashcam fans /capabilities out per (channel, stream) and, on a large DVR, several of those entries
// fall through to its per-channel Claude cascade. A whole-device sweep of a 16-channel recorder runs
// 30-60 s, past the 30 s read timeout on the manager-api /api/dashcam proxy — that is the 504. So
// slice the recorder's channels into groups and send one request per group; each stays well under it.
export const CAPABILITIES_CHANNELS_PER_REQUEST = 4;
// Dashcam caps concurrent SSH hops onto one NUC at 2 (nuc_max_concurrency). A third request in flight
// would sit on that gate, whose 45 s wait outlives our 35 s client timeout. Dashcam's gate is
// per-NUC and cross-caller, so ours has to be too: the capabilities page renders one panel per
// capture device and a store's recorders normally share one NUC, so a per-call limit would let two
// panels put four requests on the same gate.
export const CAPABILITIES_REQUEST_CONCURRENCY = 2;

/** Split `items` into consecutive groups of at most `size`. */
export function chunkItems(items, size) {
  const groups = [];
  for (let i = 0; i < items.length; i += size) {
    groups.push(items.slice(i, i + size));
  }
  return groups;
}

/** Slot numbers a /capabilities body asks for, or null when it sweeps the whole device. */
export function slotNumbersOfCapabilitiesPayload(payload) {
  const refs = payload?.channel;
  if (!Array.isArray(refs) || refs.length === 0) return null;
  return refs.map((r) => r?.slot_number).filter((n) => Number.isFinite(Number(n)));
}

/**
 * Build the POST /capabilities bodies covering every VM-configured channel, one body per group of
 * CAPABILITIES_CHANNELS_PER_REQUEST slots. Returns a single body with no `channel` — a whole-device
 * sweep — when the recorder exposes no slot list (IP cameras, or no finite channel_number).
 */
export async function buildChunkedCapabilitiesRequestBodies(
  videoRecorder,
  { localInference = false } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const ch = defaultHostPlaceholderChannel(videoRecorder);
  const body = { ...base, device_host: applyDeviceHostChannelPlaceholder(base.device_host, ch) };
  const refs = channelRefsFromVideoRecorder(videoRecorder);
  if (!refs) return [body];
  return chunkItems(refs, CAPABILITIES_CHANNELS_PER_REQUEST).map((channel) => ({ ...body, channel }));
}

/** The NUC a /capabilities body routes through — the same identity Dashcam keys its gate on. */
export function nucKeyOfCapabilitiesPayload(payload) {
  return String(payload?.machine_vpn_ip || payload?.machine_name || "");
}

// Shared by every postCapabilitiesChunks call in the page, so panels for recorders behind one NUC
// queue against each other instead of each getting its own allowance. Entries are dropped once idle.
const nucSlots = new Map();

function acquireNucSlot(key) {
  let state = nucSlots.get(key);
  if (!state) {
    state = { active: 0, waiting: [] };
    nucSlots.set(key, state);
  }
  if (state.active < CAPABILITIES_REQUEST_CONCURRENCY) {
    state.active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => state.waiting.push(resolve));
}

function releaseNucSlot(key) {
  const state = nucSlots.get(key);
  if (!state) return;
  // Hand the slot straight to the next waiter — `active` is unchanged because the slot transfers.
  const next = state.waiting.shift();
  if (next) {
    next();
    return;
  }
  state.active -= 1;
  if (state.active === 0) nucSlots.delete(key);
}

/**
 * POST each body, holding at most CAPABILITIES_REQUEST_CONCURRENCY at a time per NUC across the whole
 * page, and invoking `onResult` as each lands so the caller can fill its grid progressively. Never
 * rejects: a failed group is reported on its own entry as `{ error }` and the rest still run.
 */
export async function postCapabilitiesChunks(payloads, { signal, onResult } = {}) {
  const results = await Promise.all(
    payloads.map(async (payload) => {
      const key = nucKeyOfCapabilitiesPayload(payload);
      await acquireNucSlot(key);
      try {
        if (signal?.aborted) return null;
        let result;
        try {
          const { data } = await postCapabilities(payload, { signal });
          result = { payload, data, error: null };
        } catch (error) {
          result = { payload, data: null, error };
        }
        if (!signal?.aborted) onResult?.(result);
        return result;
      } finally {
        releaseNucSlot(key);
      }
    })
  );
  return results.filter(Boolean);
}

/** Build POST /capabilities JSON for one channel; omit streamType to request all streams for that channel. */
export async function buildCapabilitiesRequestBodyWithVpnTunnel(
  videoRecorder,
  { localInference = false, channel, streamType } = {},
  { signal } = {}
) {
  const base = await buildCamerasRequestBodyWithVpnTunnel(videoRecorder, { localInference }, { signal });
  const channelRef = { slot_number: channel };
  if (streamType != null && streamType !== "") {
    channelRef.stream_type = streamType;
  }
  // Use the VM channel number (Manager's channel_number) to resolve the {} IP placeholder — not the
  // Dashcam slot number. For IP cameras, channel_number maps to the last IP octet (e.g.
  // channel_number=34 → 192.168.99.34), while Dashcam slot numbers start at 1 regardless of IP.
  const hostChannel = defaultHostPlaceholderChannel(videoRecorder);
  const payload = {
    device_host: applyDeviceHostChannelPlaceholder(base.device_host, hostChannel),
    username: base.username,
    password: base.password,
    machine_name: typeof base.machine_name === "string" ? base.machine_name : "",
    ...cloudTopologyFields(base),
    channel: [channelRef],
    device_api_port: base.device_api_port,
  };
  if (base.machine_vpn_ip) {
    payload.machine_vpn_ip = base.machine_vpn_ip;
  }
  return payload;
}

/**
 * Map Dashcam stream capabilities to the shape expected by the DVR panel UI.
 * Dashcam OpenAPI uses `supported_bitrate_control`; the panel reads `supported_bitrate_control_modes`.
 * Dahua already returns CBR/VBR via the API field — no dashcam change required for bitrate control.
 */
export function normalizeStreamCapabilities(stream) {
  if (stream == null || typeof stream !== "object") return stream;
  const modes = stream.supported_bitrate_control_modes ?? stream.supported_bitrate_control ?? [];
  return {
    ...stream,
    supported_bitrate_control_modes: Array.isArray(modes) ? modes : [],
  };
}

/** Apply a single-channel /capabilities response onto existing grouped rows. */
export function mergeChannelRowsFromCapabilitiesPatch(prevRows, data, channel, streamType) {
  if (!Array.isArray(prevRows) || prevRows.length === 0) return prevRows;
  const incoming = channelRowsFromCapabilitiesResponse(data);
  const patch = incoming.find((r) => r.channel === channel);
  if (!patch) return prevRows;
  return prevRows.map((row) => {
    if (row.channel !== patch.channel) return row;
    const next = {
      ...row,
      degraded_reason: patch.degraded_reason != null ? patch.degraded_reason : row.degraded_reason,
      probe_outcome: patch.probe_outcome != null ? patch.probe_outcome : row.probe_outcome,
      hints: Array.isArray(patch.hints) ? patch.hints : row.hints ?? [],
    };
    if (streamType === "main") {
      next.main = patch.main;
    } else if (streamType === "sub") {
      next.sub = patch.sub;
    }
    return next;
  });
}

/** Placeholder rows from Manager store data so the capabilities grid can render before Dashcam. */
export function channelPlaceholderRowsFromStore(videoRecorder) {
  const chs = videoRecorder?.channels;
  if (!Array.isArray(chs) || chs.length === 0) return null;
  const rows = chs
    .map((c) => {
      if (c == null || typeof c !== "object") return null;
      const channelNum = Number(c.channel_number);
      if (!Number.isFinite(channelNum) || channelNum < 1) return null;
      const rawName = c.channel_name;
      const channelName = typeof rawName === "string" && rawName.trim() ? rawName.trim() : null;
      return {
        channel: channelNum,
        channel_name: channelName,
        main: null,
        sub: null,
        degraded_reason: null,
        probe_outcome: null,
        hints: [],
      };
    })
    .filter(Boolean);
  rows.sort((a, b) => a.channel - b.channel);
  return rows.length ? rows : null;
}

/** Copy channel_name from store placeholders onto API rows (same channel index). */
export function mergeChannelNamesFromPlaceholders(apiRows, placeholderRows) {
  if (!Array.isArray(apiRows) || apiRows.length === 0) return apiRows;
  if (!Array.isArray(placeholderRows) || placeholderRows.length === 0) return apiRows;
  const nameByChannel = new Map(
    placeholderRows.map((r) => [r.channel, r.channel_name != null ? r.channel_name : null])
  );
  return apiRows.map((r) => ({
    ...r,
    channel_name: nameByChannel.has(r.channel) ? nameByChannel.get(r.channel) : r.channel_name,
  }));
}

/**
 * Map Dashcam CapabilitiesResponse.channels to main/sub rows per channel.
 * When `vmChannelNumbers` (a Set of channel numbers) is provided, only channels present in VM
 * are included — DVR-only channels are dropped.
 */
export function channelRowsFromCapabilitiesResponse(data, vmChannelNumbers) {
  if (!data || typeof data !== "object") return [];
  const { channels } = data;
  if (!Array.isArray(channels)) return [];
  const rows = [];
  channels.forEach((ch) => {
    if (ch == null || typeof ch !== "object") return;
    const chNum = ch.channel;
    if (chNum === null || chNum === undefined || chNum === "") return;
    const channelNum = Number(chNum);
    if (!Number.isFinite(channelNum)) return;
    const degradedReason = ch.degraded_reason != null && ch.degraded_reason !== "" ? ch.degraded_reason : null;
    const probeOutcome = ch.probe_outcome != null && ch.probe_outcome !== "" ? ch.probe_outcome : null;
    const row = {
      channel: channelNum,
      main: null,
      sub: null,
      degraded_reason: degradedReason,
      probe_outcome: probeOutcome,
      hints: Array.isArray(ch.hints) ? ch.hints : [],
    };
    const streams = Array.isArray(ch.streams) ? ch.streams : [];
    streams.forEach((stream) => {
      if (stream == null || typeof stream !== "object") return;
      const st = String(stream.stream_type || "").toLowerCase();
      const normalized = normalizeStreamCapabilities(stream);
      if (st === "main") row.main = normalized;
      else if (st === "sub") row.sub = normalized;
    });
    rows.push(row);
  });
  // Filter after fetch: the batch /capabilities endpoint returns all DVR channels; we drop
  // channels not configured in VM here rather than trying to restrict the request payload,
  // which the API does not support at the batch level.
  const filtered =
    vmChannelNumbers instanceof Set && vmChannelNumbers.size > 0
      ? rows.filter((r) => vmChannelNumbers.has(r.channel))
      : rows;
  filtered.sort((a, b) => a.channel - b.channel);
  return filtered;
}

/**
 * Map Dashcam CamerasResponse.cameras to main/sub rows per channel (current encoder config).
 * Channel selection happens in the request (see channelRefsFromVideoRecorder), so the response is
 * already scoped to VM channels. Rows Dashcam echoes for requested-but-missing slots carry a
 * `not_found` payload and are skipped here.
 */
export function channelRowsFromGatherResponse(data) {
  if (!data || typeof data !== "object") return [];
  const { cameras } = data;
  if (!Array.isArray(cameras)) return [];
  const byChannel = new Map();
  cameras.forEach((cam) => {
    if (cam == null || typeof cam !== "object") return;
    if (cam.not_found != null) return;
    const chNum = Number(cam.channel);
    if (!Number.isFinite(chNum)) return;
    if (!byChannel.has(chNum)) {
      byChannel.set(chNum, { channel: chNum, main: null, sub: null });
    }
    const row = byChannel.get(chNum);
    const st = String(cam.stream_type || "main").toLowerCase();
    if (st === "main") row.main = cam;
    else if (st === "sub") row.sub = cam;
  });
  const rows = Array.from(byChannel.values());
  rows.sort((a, b) => a.channel - b.channel);
  return rows;
}
